'use client'

import React, { useCallback, useRef, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useRouter } from 'next/navigation'
import ReCAPTCHA from 'react-google-recaptcha'

import { Button } from '@/components/ui/button'
import { contactFormFields } from '@/blocks/ContactForm/FormFields'
import { fields as standardFields } from '@/blocks/Form/fields'
import { getClientSideURL } from '@/utilities/getURL'
import type { LexicalFormBlock } from '@/payload-types'

export function LexicalFormBlockClient({ title, subtitle, form: formFromProps }: LexicalFormBlock) {
  const formID = typeof formFromProps === 'string' ? formFromProps : formFromProps?.id
  const confirmationMessage =
    typeof formFromProps === 'object' ? formFromProps.confirmationMessage : undefined
  const confirmationType =
    typeof formFromProps === 'object' ? formFromProps.confirmationType : undefined
  const redirect = typeof formFromProps === 'object' ? formFromProps.redirect : undefined
  const submitButtonLabel =
    typeof formFromProps === 'object' ? formFromProps.submitButtonLabel : undefined
  const [requireRecaptcha, setRequireRecaptcha] = useState<boolean | undefined>(
    typeof formFromProps === 'object' ? (formFromProps as any).requireRecaptcha : undefined,
  )

  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY

  React.useEffect(() => {
    const id = typeof formFromProps === 'string' ? formFromProps : formFromProps?.id
    if (id && requireRecaptcha === undefined && typeof formFromProps === 'object') {
      fetch(`${getClientSideURL()}/api/forms/${id}?depth=0`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data: any) => {
          if (data?.requireRecaptcha !== undefined) {
            setRequireRecaptcha(Boolean(data.requireRecaptcha))
          }
        })
        .catch(() => {})
    }
  }, [formFromProps, requireRecaptcha])

  const formMethods = useForm({
    defaultValues:
      typeof formFromProps === 'object' ? formFromProps.fields || undefined : undefined,
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false)
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()
  const router = useRouter()

  const onSubmit = useCallback(
    (data: any) => {
      const submit = async () => {
        setError(undefined)
        const captchaValue = recaptchaRef.current ? recaptchaRef.current.getValue() : undefined
        if (requireRecaptcha && !captchaValue) {
          setError({ message: 'Bitte bestätigen Sie, dass Sie kein Roboter sind.' })
          return
        }
        setIsLoading(true)
        const formData = new FormData()
        formData.append('form', formID || '')
        if (captchaValue) formData.append('recaptcha', captchaValue)
        Object.entries(data || {}).forEach(([name, value]) => {
          if (value instanceof FileList) {
            Array.from(value).forEach((f) => formData.append(name, f))
            return
          }
          if (Array.isArray(value) && value.length && value[0] instanceof File) {
            value.forEach((f: File) => formData.append(name, f))
            return
          }
          const v =
            typeof value === 'string' || value instanceof Blob
              ? value
              : typeof value === 'number' || typeof value === 'boolean'
                ? String(value)
                : (value ?? '')
          formData.append(name, v as any)
        })

        try {
          const req = await fetch(`${getClientSideURL()}/api/submit-form`, {
            method: 'POST',
            body: formData,
          })
          const res = await req.json()
          if (req.status >= 400) {
            setIsLoading(false)
            setError({
              message: res.error || res.errors?.[0]?.message || 'Interner Serverfehler',
              status: res.status || req.status,
            })
            recaptchaRef.current?.reset()
            return
          }
          setIsLoading(false)
          setHasSubmitted(true)
          recaptchaRef.current?.reset()
          if (confirmationType === 'redirect' && redirect?.url) {
            router.push(redirect.url)
          }
        } catch (e) {
          setIsLoading(false)
          setError({ message: 'Etwas ist schief gelaufen.' })
          recaptchaRef.current?.reset()
        }
      }
      void submit()
    },
    [router, formID, redirect, confirmationType, requireRecaptcha],
  )

  return (
    <div className="my-14 md:my-16">
      {(title || subtitle) && (
        <div className="mb-6">
          {title && (
            <h3 className="text-2xl md:text-3xl font-bold uppercase font-metropolis text-text-primary mb-2">
              {title}
            </h3>
          )}
          {subtitle && <p className="body-base text-text-secondary leading-relaxed">{subtitle}</p>}
        </div>
      )}

      <FormProvider {...formMethods}>
        {hasSubmitted && confirmationType === 'message' && confirmationMessage ? (
          <div className="p-4 border border-border-brand bg-brand-surface-1 text-text-primary">
            <p>Vielen Dank! Wir melden uns innert 24 Stunden.</p>
          </div>
        ) : (
          <form id={formID} onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {typeof formFromProps === 'object' &&
              formFromProps?.fields?.map((field, index) => {
                const ContactField: React.FC<any> =
                  contactFormFields[field.blockType as keyof typeof contactFormFields]
                const StandardField: React.FC<any> =
                  standardFields[field.blockType as keyof typeof standardFields]
                const Field = ContactField || StandardField
                if (!Field) return null
                return (
                  <Field
                    key={index}
                    form={formFromProps}
                    {...field}
                    {...formMethods}
                    control={control}
                    errors={errors}
                    register={register}
                  />
                )
              })}

            {error && (
              <div className="p-3 border border-red-300 bg-red-50 text-red-800 text-sm">
                <strong>{error.status || 'Fehler'}:</strong> {error.message}
              </div>
            )}

            {requireRecaptcha && siteKey && (
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={siteKey}
                className="overflow-hidden"
              />
            )}

            <Button
              form={formID}
              type="submit"
              variant="primary"
              color="brand"
              loading={isLoading}
              disabled={isLoading}
            >
              {submitButtonLabel || 'Senden'}
            </Button>
          </form>
        )}
      </FormProvider>
    </div>
  )
}
